<template>
  <v2-modal
    :show="show"
    :title="isEnableCreateWishlist || !wishlists.length ? `Enter ${getLabelText} Name` : `My ${getLabelText}s`"
    size="sm"
    @close="closeModal"
  >
    <!-- Wishlist List View -->
    <div v-if="!isEnableCreateWishlist && !!wishlists.length">
      <!-- Search -->
      <div class="v2-wishlist-list__search">
        <svg
          class="v2-wishlist-list__search-icon"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <circle
            cx="11"
            cy="11"
            r="8"
          />
          <line
            x1="21"
            y1="21"
            x2="16.65"
            y2="16.65"
          />
        </svg>
        <v2-input
          :value="search"
          :placeholder="'Search ' + getLabelText"
          @input="search = $event"
        />
      </div>

      <!-- List -->
      <div class="v2-wishlist-list__items">
        <!-- None option -->
        <button
          class="v2-wishlist-list__item"
          :class="{ 'v2-wishlist-list__item--selected': selectedWishlist && selectedWishlist._id === '' }"
          @click="onUnsetWishlistSelection"
        >
          None
        </button>

        <!-- My Wishlists -->
        <div
          v-if="filteredMyWishlists.length"
          class="v2-wishlist-list__section-title"
        >
          My {{ getLabelText }}s:
        </div>
        <button
          v-for="wishlist in filteredMyWishlists"
          :key="wishlist._id"
          class="v2-wishlist-list__item"
          :class="{ 'v2-wishlist-list__item--selected': selectedWishlist && selectedWishlist._id === wishlist._id }"
          @click="onSelectWishlist(wishlist)"
        >
          {{ wishlist.name }}
        </button>

        <!-- Shared by Brand -->
        <div
          v-if="filteredBrandWishlists.length && !isBrand"
          class="v2-wishlist-list__section-title"
        >
          Shared by Brand:
        </div>
        <button
          v-for="wishlist in filteredBrandWishlists"
          :key="'brand-' + wishlist._id"
          class="v2-wishlist-list__item"
          :class="{ 'v2-wishlist-list__item--selected': selectedWishlist && selectedWishlist._id === wishlist._id }"
          @click="onSelectWishlist(wishlist)"
        >
          {{ wishlist.name }}
        </button>

        <!-- Empty state -->
        <div
          v-if="!filteredMyWishlists.length && !filteredBrandWishlists.length"
          class="v2-wishlist-list__empty"
        >
          {{ getLabelText }}s not found
        </div>
      </div>

      <!-- Add new button -->
      <v2-button
        v-if="!isCreateWishlistBtnDisabled"
        class="v2-wishlist-list__add-btn"
        @click="addNewWishlist"
      >
        Add new {{ getLabelText.toLowerCase() }}
      </v2-button>
    </div>

    <!-- Create Wishlist View -->
    <div v-else>
      <div class="v2-wishlist-list__form">
        <label class="v2-wishlist-list__form-label">
          {{ getLabelText }} name
        </label>
        <v2-input
          :value="selectionName"
          :placeholder="`Enter ${getLabelText.toLowerCase()} name (optional)`"
          @input="onNameInput"
        />
      </div>
      <v2-button
        class="v2-wishlist-list__create-btn"
        @click="createSelectionWithName"
      >
        Create
      </v2-button>
    </div>
  </v2-modal>
</template>

<script>
import V2Modal from '@/components/v2/V2Modal.vue'
import V2Input from '@/components/v2/V2Input.vue'
import V2Button from '@/components/v2/V2Button.vue'
import UserRoleMixin from '@/views/apps/UserRoleMixin.vue'
import { apiToastSuccess } from '@core/utils/toast'
import { mapState } from 'vuex'
import { SET_IS_ENABLE_CREATE_WISHLIST } from '@/store/modules/wishlist.module'
import constants from '@/constants'
import analytics from '@/@core/utils/analytics'
import { debounce } from 'lodash'

const { TRACKS: { ACTIONS } } = constants

export default {
  name: 'V2WishlistListModal',
  components: {
    V2Modal,
    V2Input,
    V2Button,
  },
  mixins: [UserRoleMixin],
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    wishlists: {
      type: Array,
      default: () => [],
    },
    selectedWishlist: {
      type: Object,
      default: () => ({}),
    },
    selectedProducts: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      selectionName: '',
      selectedWishlistData: {},
      search: '',
    }
  },
  computed: {
    getLabelText() {
      return 'Wishlist'
    },
    filteredMyWishlists() {
      const searchTerm = this.search?.trim().toLowerCase()
      return this.wishlists.filter(
        wishlist => !wishlist?.isBrandInitiated && wishlist.name?.toLowerCase().includes(searchTerm),
      )
    },
    filteredBrandWishlists() {
      const searchTerm = this.search?.trim().toLowerCase()
      return this.wishlists.filter(
        wishlist => wishlist?.isBrandInitiated && wishlist.name?.toLowerCase().includes(searchTerm),
      )
    },
    ...mapState({
      isEnableCreateWishlist: state => state.wishlist.isEnableCreateWishlist,
      isCreateWishlistBtnDisabled: state => state.wishlist.isCreateWishlistBtnDisabled,
    }),
  },
  methods: {
    addNewWishlist() {
      this.$store.commit(SET_IS_ENABLE_CREATE_WISHLIST, true)
      const trackName = this.isRetailer
        ? ACTIONS.COLLECTIONS.RETAILER_CLICKS_COLLECTION_PAGE_ADD_NEW_WISHLIST_BUTTON
        : ACTIONS.COLLECTIONS.BRAND_CLICKS_COLLECTION_PAGE_ADD_NEW_SELECTION_BUTTON
      analytics.track(trackName)
    },
    onUnsetWishlistSelection() {
      if (this.isRetailer && this.selectedWishlist._id !== '') {
        this.$emit('unset-wishlist-selection', { wishlistId: '' })
      }
      this.closeModal()
    },
    onSelectWishlist(wishlist = {}) {
      this.closeModal()
      if (this.isCreateWishlistBtnDisabled) {
        this.selectedWishlistData._id !== wishlist?._id
          && apiToastSuccess(`${this.getLabelText} "${wishlist?.name}" selected`)
      } else {
        apiToastSuccess(`Products added to the ${this.getLabelText?.toLowerCase()} "${wishlist?.name}"`)
      }
      this.$emit('move-to-wishlist', {
        wishlistId: wishlist?._id || null,
        wishlist: wishlist ?? this.selectedWishlistData,
      })
      this.selectedWishlistData = wishlist
    },
    createSelectionWithName() {
      const trackName = this.isRetailer
        ? ACTIONS.COLLECTIONS.RETAILER_CLICKS_COLLECTION_PAGE_ADD_NEW_WISHLIST_CREATE_BUTTON
        : ACTIONS.COLLECTIONS.BRAND_CLICKS_COLLECTION_PAGE_ADD_NEW_SELECTION_CREATE_BUTTON
      analytics.track(trackName, {
        selectionName: this.selectionName,
      })
      const emitData = {
        wishlistName: this.selectionName,
        wishlist: this.selectedWishlistData,
        isCreateWishlist: true,
      }
      this.closeModal()
      this.$emit('should-change-wishlist')
      this.$emit('move-to-wishlist', emitData)
    },
    closeModal() {
      this.resetProperties()
      this.$emit('close')
    },
    resetProperties() {
      if (!this.isEnableCreateWishlist) {
        setTimeout(() => this.$emit('should-change-wishlist'), 200)
      } else {
        const trackName = this.isRetailer
          ? ACTIONS.COLLECTIONS.RETAILER_COLLECTIONS_PAGE_CLOSES_ADD_NEW_SELECTION_MODAL
          : ACTIONS.COLLECTIONS.BRAND_COLLECTIONS_PAGE_CLOSES_ADD_NEW_SELECTION_MODAL
        analytics.track(trackName, {
          [`${this.isRetailer ? 'wishlistName' : 'selectionName'}`]: this.selectionName,
          products: this.selectedProducts,
        })
      }
      this.$store.commit(SET_IS_ENABLE_CREATE_WISHLIST, false)
      this.selectionName = ''
      this.search = ''
    },
    onNameInput(value) {
      this.selectionName = value
      this.debouncedTrackSelectionName(value, this.isRetailer)
    },
    debouncedTrackSelectionName: debounce((name, isRetailer) => {
      const trackName = isRetailer
        ? ACTIONS.COLLECTIONS.RETAILER_ENTERS_COLLECTION_PAGE_ADD_NEW_WISHLIST_NAME
        : ACTIONS.COLLECTIONS.BRAND_ENTERS_COLLECTION_PAGE_ADD_NEW_SELECTION_NAME
      const dataBeingSent = {
        [`${isRetailer ? 'wishlistName' : 'selectionName'}`]: name,
      }
      analytics.track(trackName, dataBeingSent)
    }, 500),
  },
}
</script>

<style lang="scss" scoped>
.v2-wishlist-list {
  &__search {
    position: relative;
    margin-bottom: var(--v2-space-3);
  }

  &__search-icon {
    position: absolute;
    top: 50%;
    left: var(--v2-space-3);
    transform: translateY(-50%);
    pointer-events: none;
    z-index: 1;
    color: var(--v2-muted-foreground);
  }

  &__search ::v-deep .v2-input {
    padding-left: var(--v2-space-8);
  }

  &__items {
    max-height: 320px;
    overflow-y: auto;
    margin-bottom: var(--v2-space-3);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
  }

  &__section-title {
    padding: var(--v2-space-2) var(--v2-space-3);
    font-size: 0.75rem;
    font-weight: 700;
    color: var(--v2-muted-foreground);
    background: var(--v2-muted);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  &__item {
    display: block;
    width: 100%;
    padding: var(--v2-space-2-5) var(--v2-space-3);
    background: none;
    border: none;
    border-bottom: 1px solid var(--v2-border);
    cursor: pointer;
    font-family: var(--v2-font-sans);
    font-size: 0.875rem;
    color: var(--v2-foreground);
    text-align: left;
    transition: background 0.1s ease;

    &:last-child {
      border-bottom: none;
    }

    &:hover {
      background: var(--v2-muted);
    }

    &--selected {
      background: var(--v2-primary);
      color: var(--v2-primary-foreground);

      &:hover {
        background: var(--v2-primary);
      }
    }
  }

  &__empty {
    padding: var(--v2-space-4);
    text-align: center;
    font-size: 0.875rem;
    color: var(--v2-muted-foreground);
  }

  &__add-btn {
    width: 100%;
  }

  &__form {
    margin-bottom: var(--v2-space-4);
  }

  &__form-label {
    display: block;
    font-size: 0.875rem;
    font-weight: 500;
    color: var(--v2-foreground);
    margin-bottom: var(--v2-space-2);
  }

  &__create-btn {
    width: 100%;
  }
}
</style>
